import React, { useState, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import { DatePicker, TimePicker, ConfigProvider } from "antd";
import thTH from "antd/locale/th_TH";
import dayjs from "dayjs";
import SelectStepModal from "../Modal/Contract/SelectStepModal";
import AddJobWorkModal from "../Modal/AddJobWorkModal";
import { formatDate, formatDateMonthYear } from "../../../utils/DateUtils";
import api from "../../../utils/axios";
import { getDataWithComp } from "../../../utils/SamuiUtils";

const uuid = () =>
  crypto.randomUUID
    ? crypto.randomUUID()
    : "id-" +
      Date.now().toString(36) +
      "-" +
      Math.random().toString(36).slice(2, 10);

export default function JobWorkTable({
  works = [],
  setWorks,
  onSaveWorks,
  contractServices = [],
  contractId = null,
  tempEquipment = [],
  assignedRoundMap = {},
  successStatuses = new Set([]),
  endDate = null,
  onAssigned,
}) {
  // Debug: Check if onSaveWorks is provided
  useEffect(() => {
  }, [onSaveWorks]);
  const [serviceList, setServiceList] = useState([]);
  const [openGroup, setOpenGroup] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalServiceData, setModalServiceData] = useState(null);
  const [currentRoundIndex, setCurrentRoundIndex] = useState(null);
  const [currentServiceIndex, setCurrentServiceIndex] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [hasUnsavedSteps, setHasUnsavedSteps] = useState(false); // Track if there are unsaved steps from modal
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track if there are unsaved changes
  const [itemList, setItemList] = useState([]);
  const [tempEquipGroupIds, setTempEquipGroupIds] = useState(new Set());

  // Edit-assign modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRoundIdx, setEditRoundIdx] = useState(null);
  const [editForm, setEditForm] = useState({ dateMode: "month", date: "", month: "", year: "", teamId: "", timeStart: "08:00", timeEnd: "09:00" });
  const [teamList, setTeamList] = useState([]);
  const [editLoading, setEditLoading] = useState(false);

const activeTempEquipment = tempEquipment.filter((item) => Number(item?.status) !== 4);
  const activeTempRoundNumbers = activeTempEquipment
    .map((item) => Number(item?.round_no))
    .filter((roundNo) => Number.isFinite(roundNo));
  const activeTempRoundSet = new Set(activeTempRoundNumbers);
  const isTempEquipmentStep = (step) => {
    const groupFromStep =
      step?.itemGroup != null && step?.itemGroup !== "" ? String(step.itemGroup) : null;
    const groupFromItem =
      step?.itemId != null && step?.itemId !== ""
        ? String(
            itemList.find((it) => String(it.Item_Id) === String(step.itemId))?.Item_Group ?? ""
          )
        : "";
    const resolvedGroupId = groupFromStep || (groupFromItem || null);

    if (resolvedGroupId && tempEquipGroupIds.has(String(resolvedGroupId))) {
      return true;
    }

    return false;
  };

  const shouldShowTempEquipmentLabel = (roundNo) => {
    if (activeTempEquipment.length === 0) return false;

    const parsedRoundNo = Number(roundNo);
    if (!Number.isFinite(parsedRoundNo)) return false;
    return activeTempRoundSet.has(parsedRoundNo);
  };

  const servicedStatuses = new Set([
    "Check in ทำงาน", "working", "On-Site",
    "ปิดงาน รอเซ็น", "ปิดงาน เซ็นแล้ว", "ปิดงาน",
    "รอส่งงาน", "ส่งงาน", "ส่งงาน (Checkout)", "สำเร็จ",
  ]);

  const getAssignedInfo = (round) => {
    const byId = round?.Round_Id != null ? assignedRoundMap[`id:${round.Round_Id}`] : null;
    const byNo = round?.no != null ? assignedRoundMap[`no:${round.no}`] : null;
    return byId || byNo || null;
  };

  const isRoundAssigned = (round) => {
    const assigned = getAssignedInfo(round);
    return !!(assigned?.Job_Date || assigned?.Round_Date);
  };

  const isRoundServiced = (round) => {
    const assigned = getAssignedInfo(round);
    return !!(assigned && servicedStatuses.has(String(assigned.jobStatus || "").trim()));
  };

  const getAssignedDateLabel = (round) => {
    const assigned = getAssignedInfo(round);
    const assignedDate = assigned?.Job_Date || assigned?.Round_Date || null;

    if (assignedDate) {
      return formatDate(assignedDate) || formatDateMonthYear(assignedDate) || assignedDate;
    }

    const plannedDate = round?.date || null;
    if (!plannedDate) return "-";
    return formatDateMonthYear(plannedDate) || formatDate(plannedDate) || plannedDate;
  };

  // ดึงทีมช่างสำหรับ edit modal
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const compId = localStorage.getItem("company");
        const res = await api.get(`/get-teams?Comp_Id=${compId}`);
        if (res.data?.status === "OK") {
          setTeamList(
            (res.data.result || [])
              .filter((t) => t.Team_Status === "A")
              .map((t) => ({ id: t.Team_Id, name: t.Team_Name }))
          );
        }
      } catch {}
    };
    fetchTeams();
  }, []);

  const editRound = (rIdx) => {
    const today = new Date().toISOString().split("T")[0];
    const roundDate = works[rIdx]?.date || "";
    const ceYear = roundDate ? parseInt(roundDate.split("-")[0]) : new Date().getFullYear();
    const initMonth = roundDate ? roundDate.split("-")[1] : String(new Date().getMonth() + 1).padStart(2, "0");
    const initYear = String(ceYear + 543);
    setEditRoundIdx(rIdx);
    setEditForm({ dateMode: "month", date: today, month: initMonth, year: initYear, teamId: "", timeStart: "08:00", timeEnd: "09:00" });
    setShowEditModal(true);
  };

  const handleAssign = async () => {
    const { date, teamId, timeStart, timeEnd } = editForm;
    if (!date || !teamId || !timeStart || !timeEnd) {
      Swal.fire({ icon: "warning", title: "กรุณากรอกข้อมูลให้ครบ", confirmButtonText: "ตกลง" });
      return;
    }
    setEditLoading(true);
    try {
      const compId = localStorage.getItem("company");

      // ตรวจสอบ conflict ในปฏิทิน
      const conflictRes = await api.get("/get-assigned-jobs", {
        params: { comp_id: compId, team_id: teamId, start_date: date, end_date: date },
      });
      if (conflictRes.data?.status === "OK" && Array.isArray(conflictRes.data.result)) {
        const toMin = (t) => { const [h, m] = String(t).split(":").map(Number); return h * 60 + m; };
        const newS = toMin(timeStart);
        const newE = toMin(timeEnd);
        const hasConflict = conflictRes.data.result.some((job) => {
          if (!job.Time_Start || !job.Time_End) return false;
          return newS < toMin(job.Time_End) && newE > toMin(job.Time_Start);
        });
        if (hasConflict) {
          Swal.fire({
            icon: "warning",
            title: "เวลาซ้ำในปฏิทิน",
            text: "ทีมนี้มีงานในช่วงเวลาที่เลือกแล้ว กรุณาเลือกเวลาอื่น",
            confirmButtonText: "ตกลง",
          });
          setEditLoading(false);
          return;
        }
      }

      const round = works[editRoundIdx];
      const response = await api.post("/assign-job-calendar", {
        Contract_Id: contractId,
        Team_Id: Number(teamId),
        Job_Date: date,
        Time_Start: timeStart,
        Time_End: timeEnd,
        Comp_Id: compId ? parseInt(compId) : null,
        Job_Status: "ลงงาน",
        ...(round?.Round_Id != null ? { Round_Id: round.Round_Id } : {}),
        ...(round?.no != null ? { Round_No: round.no } : {}),
      });

      if (response.data?.status === "OK") {
        Swal.fire({ icon: "success", title: "Assign สำเร็จ", timer: 1500, showConfirmButton: false });
        setShowEditModal(false);
        onAssigned?.();
      } else {
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: response.data?.message || "ไม่สามารถ assign ได้",
          confirmButtonText: "ตกลง",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: err.response?.data?.message || "ไม่สามารถ assign ได้",
        confirmButtonText: "ตกลง",
      });
    }
    setEditLoading(false);
  };

  // ดึงข้อมูลบริการและขั้นตอนทั้งหมด
  useEffect(() => {
    const fetchServicesWithSteps = async () => {
      try {
        // ดึง services ทั้งหมดพร้อม steps
        const compId = localStorage.getItem("company");
        const res = await api.get(`/get-services-with-steps${compId ? `?Comp_Id=${compId}` : ""}`);
        
        if (res.data.status === "OK") {
          const services = (res.data.result || []).map((service) => ({
            Service_Id: service.Service_Id,
            Service_Name: service.Service_Name,
            Steps: (service.Steps || []).map((step) => ({
              Step_Id: step.Step_Id,
              Step_Title: step.Step_Title,
              Step_Qty: step.Step_Qty,
              Step_Unit: step.Step_Unit,
              IsDefault: step.IsDefault || 0,
            })),
          }));
          
          console.log("ServiceList updated (all services):", services);
          console.log("ServiceList count:", services.length);
          setServiceList(services);
        } else {
          console.error("Failed to fetch services with steps:", res.data.message);
          setServiceList([]);
        }
      } catch (error) {
        console.error("Error fetching services with steps:", error);
        setServiceList([]);
      }
    };

    fetchServicesWithSteps();
  }, []); // ไม่ต้อง depend on contractServices แล้ว

  const materialOptions = useMemo(() => {
    const seen = new Set();
    const items = [];
    for (const service of serviceList || []) {
      for (const step of service?.Steps || []) {
        const title = String(step?.Step_Title || "").trim();
        if (!title || seen.has(title)) continue;
        seen.add(title);
        items.push({
          title,
          unit: step?.Step_Unit != null ? String(step.Step_Unit) : "",
        });
      }
    }
    return items.sort((a, b) => a.title.localeCompare(b.title, "th"));
  }, [serviceList]);

  useEffect(() => {
    const fetchItemsForWork = async () => {
      try {
        const [groups, items] = await Promise.all([
          getDataWithComp("Tb_Set_ItmGroup", "AND IsShowInWork = 1 AND Group_Status = 'Y'", {
            fields: ["Group_Id", "Group_Name"],
          }),
          getDataWithComp("Tb_Set_Item", "AND Item_Status = 'Y' ORDER BY Item_Short_Name, Item_Name", {
            fields: ["Item_Id", "Item_Name", "Item_Unit_ST", "Item_Group"],
          }),
        ]);
        const groupSet = new Set((groups || []).map((g) => String(g.Group_Id)));
        const filtered = (items || []).filter((i) => groupSet.has(String(i.Item_Group)));
        setItemList(filtered);

        try {
          const teGroupRes = await api.get("/app/temp-equip-groups");
          const teGroups = teGroupRes.data?.result || [];
          setTempEquipGroupIds(new Set(teGroups.map((g) => String(g.Group_Id))));
        } catch {
          // ถ้า fetch ไม่ได้ ปล่อยให้ tempEquipGroupIds ว่างไว้ก่อน
        }
      } catch (error) {
        console.error("Error fetching items for work:", error);
        setItemList([]);
      }
    };
    fetchItemsForWork();
  }, []);

  const toggleGroup = (index) => {
    setOpenGroup((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const openAddStepModal = (roundIndex, srvIndex, serviceName) => {
    const srv = serviceList.find((s) => s.Service_Name === serviceName);
    if (!srv) return;

    setModalServiceData(srv);
    setCurrentRoundIndex(roundIndex);
    setCurrentServiceIndex(srvIndex);
    setShowModal(true);
  };

  const addStepsFromModal = (stepList) => {
    if (!stepList || stepList.length === 0) {
      return;
    }

    // Add steps to local state only (no save to API)
    const updatedWorks = works.map((round, rIdx) => {
      if (rIdx !== currentRoundIndex) return round;

      const updatedServices = round.services.map((srv, sIdx) => {
        if (sIdx !== currentServiceIndex) return srv;

        const existRef = new Set(srv.steps.map((x) => x.refStepId || x.id));
        const newSteps = stepList
          .filter((st) => !existRef.has(st.Step_Id))
          .map((st) => ({
            id: uuid(),
            title: st.Step_Title || "",
            qty: st.Step_Qty != null ? String(st.Step_Qty) : "",
            unit: st.Step_Unit != null ? String(st.Step_Unit) : "",
            remark: "",
            isCollect: false,
            checked: true, // Auto checkbox steps that are added from modal
            refStepId: st.Step_Id,
          }));

        return { ...srv, steps: [...srv.steps, ...newSteps] };
      });

      return { ...round, services: updatedServices };
    });

    // Update local state only
    setWorks(updatedWorks);
    // Mark that there are unsaved steps from modal
    setHasUnsavedSteps(true);
  };

  const addNewJob = (job) => {
    const newJob = {
      id: uuid(),
      no: 0,
      Round_Id: null,
      date: job.assignDate,
      status: "งานเสริม",
      isDB: false,
      assignInfo: {
        date: job.assignDate,
        teamId: job.teamId,
        timeStart: job.timeStart,
        timeEnd: job.timeEnd,
      },
      services: [
        {
          id: uuid(),
          name: job.service,
          steps: job.steps.map((s) => ({
            ...s,
            id: uuid(),
            checked: true,
            isCollect: s?.isCollect === true,
            qty: s.qty != null ? String(s.qty) : "",
            unit: s.unit != null ? String(s.unit) : "",
            remark: s.remark != null ? String(s.remark) : "",
          })),
        },
      ],
    };

    setWorks((prev) => {
      const combined = [...prev, newJob];
      // เรียงตามวันที่ (ไม่มีวันที่ให้ไปอยู่ท้าย) แล้ว renumber ตามลำดับ
      combined.sort((a, b) => {
        const da = a.date || "";
        const db = b.date || "";
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        if (da < db) return -1;
        if (da > db) return 1;
        return 0;
      });
      return combined.map((w, i) => ({ ...w, no: i + 1 }));
    });
  };

  const removeStep = async (roundIndex, srvIndex, stepId) => {
    const updatedWorks = works.map((round, rIdx) => {
      if (rIdx !== roundIndex) return round;

      const updated = round.services.map((srv, sIdx) => {
        if (sIdx !== srvIndex) return srv;
        return { ...srv, steps: srv.steps.filter((st) => st.id !== stepId) };
      });

      return { ...round, services: updated };
    });

    // Update local state
    setWorks(updatedWorks);
    setHasUnsavedChanges(true);
  };

  const removeService = async (roundIndex, serviceIndex) => {
    const round = works[roundIndex];
    const service = round?.services[serviceIndex];
    
    const result = await Swal.fire({
      title: "ยืนยันการลบ",
      text: `ต้องการลบบริการ "${service?.name || ""}" หรือไม่?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
    });

    if (!result.isConfirmed) return;

    const updatedWorks = works.map((round, rIdx) => {
      if (rIdx !== roundIndex) return round;
      
      // Filter out the service at serviceIndex
      const updatedServices = round.services.filter((srv, sIdx) => sIdx !== serviceIndex);
      
      // If no services left, we might want to remove the entire round
      // But for now, just return round with empty services array
      return { ...round, services: updatedServices };
    });

    // Update local state
    setWorks(updatedWorks);
    setHasUnsavedChanges(true);
  };

  const removeRound = async (roundIndex) => {
    const round = works[roundIndex];

    const result = await Swal.fire({
      title: "ยืนยันการลบ",
      text: `ต้องการลบครั้งที่ ${round?.no || ""} ทั้งหมดหรือไม่?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
    });

    if (!result.isConfirmed) return;

    // ถ้า round นี้ assign ไปแล้ว ให้ยกเลิก calendar entry ก่อน
    const assignedInfo = getAssignedInfo(round);
    if (assignedInfo?.id) {
      try {
        await api.delete(`/remove-job-calendar/${assignedInfo.id}`);
      } catch {}
    }

    const updatedWorks = works.filter((_, rIdx) => rIdx !== roundIndex);
    const normalizedWorks = normalizeRoundOrder(updatedWorks);
    setWorks(normalizedWorks);
    setHasUnsavedChanges(true);
  };

  const inputClass =
    "w-full h-[36px] border border-gray-300 rounded-md px-2 text-[13px] focus:ring-1 focus:ring-[#E13F57]";

  const sanitizeNumericInput = (raw) => {
    const cleaned = String(raw ?? "").replace(/,/g, "").replace(/[^\d.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length <= 2) return cleaned;
    return `${parts[0]}.${parts.slice(1).join("")}`;
  };

  const formatQtyDisplay = (raw) => {
    if (raw === null || raw === undefined || raw === "") return "";
    const num = Number(String(raw).replace(/,/g, ""));
    if (Number.isNaN(num)) return "";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const normalizeRoundOrder = (rounds = []) => {
    const sorted = [...rounds].sort((a, b) => {
      const da = a?.date || "";
      const db = b?.date || "";
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      if (da < db) return -1;
      if (da > db) return 1;
      return 0;
    });
    return sorted.map((round, index) => ({ ...round, no: index + 1 }));
  };


  return (
    <div className="bg-white rounded-xl shadow-md p-6 mt-6 border border-gray-300">
      <div className="flex justify-end mb-4">
        <button
          onClick={() => {
            console.log("Opening AddJobWorkModal, serviceList:", serviceList);
            console.log("contractServices:", contractServices);
            setShowAddModal(true);
          }}
          className="bg-[#1D4ED8] hover:bg-[#163fb2] text-white px-4 py-2 rounded-lg flex items-center gap-1 shadow"
        >
          <Icon icon="mdi:plus" className="w-5 h-5" />
          เพิ่ม Job เสริม
        </button>

        <AddJobWorkModal
          isOpen={showAddModal}
          onClose={() => {
            console.log("Closing AddJobWorkModal");
            setShowAddModal(false);
          }}
          onSave={(job) => {
            console.log("Saving job:", job);
            addNewJob(job);
          }}
          serviceList={serviceList || []}
          contractServices={contractServices || []}
          currentWorksCount={works.length}
          latestRoundDate={(() => {
            const completedWorks = works.filter(
              (round) => round.isDB === true && round.Round_Id != null && round.date != null && round.date !== ""
            );
            if (completedWorks.length === 0) return null;
            const dates = completedWorks.map((round) => round.date);
            return dates.reduce((latest, date) => (!latest || date > latest ? date : latest), null);
          })()}
          existingDates={works.map((r) => r.date).filter(Boolean)}
          contractEndDate={endDate}
        />
      </div>

      {works.map((round, rIdx) => (
        <div key={round.id} className="border border-gray-300 rounded-xl mb-4">

          <div className="flex items-center justify-between bg-gray-50 px-4 py-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => toggleGroup(rIdx)}
                className="w-7 h-7 bg-white border border-gray-300 rounded-md flex items-center justify-center"
              >
                <Icon
                  icon={openGroup[rIdx] ? "mdi:chevron-up" : "mdi:chevron-down"}
                  className="w-5 h-5 text-gray-700"
                />
              </button>

              <div className="font-bold text-gray-700">ครั้งที่ {round.no}</div>

              <span
                className={`text-xs px-2 py-1 rounded-full ${
                  round.status === "งานเสริม"
                    ? "bg-orange-100 text-orange-600"
                    : "bg-green-100 text-green-600"
                }`}
              >
                {round.status}
              </span>

              <div className="text-gray-600 flex items-center gap-1 flex-wrap">
                {isRoundAssigned(round) && isRoundServiced(round) ? (
                  <>วันที่เข้าบริการ {getAssignedDateLabel(round)}</>
                ) : isRoundAssigned(round) ? (
                  <>
                    <Icon icon="mdi:clock-outline" className="w-4 h-4 text-gray-500" />
                    <span>
                      รอเข้าบริการวันที่ {getAssignedDateLabel(round)}
                      {getAssignedInfo(round)?.teamName ? ` ทีม ${getAssignedInfo(round).teamName}` : ""}
                    </span>
                  </>
                ) : (
                  <>
                    <Icon icon="mdi:clock-outline" className="w-4 h-4 text-gray-500" />
                    <span>รอเข้าบริการ{round.date ? ` ${formatDateMonthYear(round.date) || round.date}` : ""}</span>
                  </>
                )}
                {(() => {
                  const tempSteps = round.services.flatMap((srv) =>
                    srv.steps.filter((st) => isTempEquipmentStep(st))
                  );
                  if (tempSteps.length === 0) return null;
                  return (
                    <>
                      <span className="text-gray-400 mx-1">|</span>
                      <span className="text-amber-700 font-medium text-xs">อุปกรณ์ชั่วคราว:</span>
                      {tempSteps.map((st) => (
                        <span
                          key={st.id}
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            st.isCollect
                              ? "bg-green-100 text-green-700"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {st.material || st.itemName || st.title}
                          {st.qty ? ` x${st.qty}` : ""}
                          {st.unit ? ` ${st.unit}` : ""}
                          {st.isCollect ? " (เก็บคืน)" : " (วางไว้)"}
                        </span>
                      ))}
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {!isRoundServiced(round) && (
                <>
                  <button
                    onClick={() => editRound(rIdx)}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 rounded flex items-center gap-1"
                    title="แก้ไขครั้งที่"
                  >
                    <Icon icon="mdi:calendar-edit" className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => removeRound(rIdx)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1"
                    title="ลบครั้งที่"
                  >
                    <Icon icon="mdi:delete" className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
          </div>

          {openGroup[rIdx] && (
            <div className={`p-4 bg-white border-t relative ${isRoundServiced(round) ? "pointer-events-none select-none opacity-80" : ""}`}>

              {round.services.map((srv, sIdx) => (
                <div key={srv.id} className="mb-6">

                  <div className="flex justify-between mb-2">
                    <div className="font-bold text-gray-800">
                      {sIdx + 1}. {srv.name}
                    </div>

                    {!isRoundServiced(round) && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openAddStepModal(rIdx, sIdx, srv.name)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1"
                        >
                          <Icon icon="mdi:plus-circle" className="w-4 h-4" />
                          เพิ่มขั้นตอน
                        </button>
                        <button
                          onClick={() => removeService(rIdx, sIdx)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1"
                        >
                          <Icon icon="mdi:delete" className="w-4 h-4" />
                          ลบบริการ
                        </button>
                      </div>
                    )}
                  </div>

                  <table className="w-full border text-sm">
                    <thead>
                      <tr className="bg-[#E13F57] text-white">
                        <th className="py-2 border px-1 w-10 text-center">เลือก</th>
                        <th className="py-2 border px-2 text-center w-[24%]">ขั้นตอนบริการ</th>
                        <th className="py-2 border px-2 text-center w-[18%]">อุปกรณ์ / สารเคมี</th>
                        <th className="py-2 border text-center w-[14%]">ปริมาณ</th>
                        <th className="py-2 border text-center w-[16%]">วางไว้ / เก็บคืน</th>
                        <th className="py-2 border text-center w-[28%]">หมายเหตุ</th>
                        <th className="py-2 border text-center"></th>
                      </tr>
                    </thead>

                    <tbody>
                      {srv.steps.map((st) => {
                        const currentRoundIdx = rIdx;
                        const currentServiceIdx = sIdx;
                        const isTempEquipmentItem = isTempEquipmentStep(st);
                        const rawItemId =
                          st.itemId != null && st.itemId !== "" ? Number(st.itemId) : null;
                        const legacyMaterialName = String(
                          st.material || st.itemName || ""
                        ).trim();
                        const hasValidItemId =
                          rawItemId != null && !Number.isNaN(rawItemId) && rawItemId > 0;
                        const itemOptions = hasValidItemId
                          ? itemList
                          : legacyMaterialName &&
                            !itemList.some((i) => i.Item_Name === legacyMaterialName)
                            ? [
                                {
                                  Item_Id: `legacy:${legacyMaterialName}`,
                                  Item_Name: legacyMaterialName,
                                  Item_Unit_ST: st.unit || "",
                                },
                                ...itemList,
                              ]
                            : itemList;
                        const selectedMaterialValue = hasValidItemId
                          ? String(rawItemId)
                          : legacyMaterialName &&
                              itemOptions.some(
                                (i) => String(i.Item_Id) === `legacy:${legacyMaterialName}`
                              )
                            ? `legacy:${legacyMaterialName}`
                            : "";
                        return (
                        <tr key={st.id} className="odd:bg-white even:bg-gray-50">

                          <td className="text-center border py-2 px-1 w-10">
                            <input 
                              type="checkbox" 
                              checked={st.checked || false} 
                              onChange={(e) => {
                                const updatedWorks = works.map((round, roundIdx) => {
                                  if (roundIdx !== currentRoundIdx) return round;
                                  const updatedServices = round.services.map((service, serviceIdx) => {
                                    if (serviceIdx !== currentServiceIdx) return service;
                                    const updatedSteps = service.steps.map((step) => {
                                      if (step.id === st.id) {
                                        return { ...step, checked: e.target.checked };
                                      }
                                      return step;
                                    });
                                    return { ...service, steps: updatedSteps };
                                  });
                                  return { ...round, services: updatedServices };
                                });
                                setWorks(updatedWorks);
                                setHasUnsavedChanges(true);
                              }}
                              className="accent-[#E13F57] cursor-pointer w-4 h-4" 
                            />
                          </td>

                          <td className="border px-2 w-[24%]">
                            <input className={inputClass} value={st.title} readOnly />
                          </td>

                          <td className="border px-2 w-[18%]">
                            <select
                              className={inputClass}
                              value={selectedMaterialValue}
                              onChange={(e) => {
                                const selectedValue = e.target.value;
                                const selectedItem = itemOptions.find(
                                  (opt) => String(opt.Item_Id) === selectedValue
                                );
                                const updatedWorks = works.map((round, roundIdx) => {
                                  if (roundIdx !== currentRoundIdx) return round;
                                  const updatedServices = round.services.map((service, serviceIdx) => {
                                    if (serviceIdx !== currentServiceIdx) return service;
                                    const updatedSteps = service.steps.map((step) => {
                                      if (step.id === st.id) {
                                        return {
                                          ...step,
                                          itemId:
                                            selectedItem &&
                                            String(selectedItem.Item_Id).startsWith("legacy:")
                                              ? null
                                              : selectedItem
                                                ? selectedItem.Item_Id
                                                : null,
                                          itemName: selectedItem ? selectedItem.Item_Name : "",
                                          material: selectedItem ? selectedItem.Item_Name : "",
                                          itemGroup: selectedItem ? selectedItem.Item_Group ?? null : null,
                                          unit: selectedItem
                                            ? selectedItem.Item_Unit_ST || ""
                                            : step.unit || "",
                                        };
                                      }
                                      return step;
                                    });
                                    return { ...service, steps: updatedSteps };
                                  });
                                  return { ...round, services: updatedServices };
                                });
                                setWorks(updatedWorks);
                                setHasUnsavedChanges(true);
                              }}
                            >
                              <option value="">-- เลือกอุปกรณ์ / สารเคมี --</option>
                              {itemOptions.map((opt) => (
                                <option key={String(opt.Item_Id)} value={String(opt.Item_Id)}>
                                  {opt.Item_Name}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className="border px-2 w-[14%]">
                            <div className="flex items-center gap-2">
                              <input
                                className={`${inputClass} text-center tabular-nums`}
                                value={formatQtyDisplay(st.qty)}
                                onChange={(e) => {
                                  const normalizedValue = sanitizeNumericInput(e.target.value);
                                  const updatedWorks = works.map((round, roundIdx) => {
                                    if (roundIdx !== currentRoundIdx) return round;
                                    const updatedServices = round.services.map((service, serviceIdx) => {
                                      if (serviceIdx !== currentServiceIdx) return service;
                                      const updatedSteps = service.steps.map((step) => {
                                        if (step.id === st.id) {
                                          return { ...step, qty: normalizedValue };
                                        }
                                        return step;
                                      });
                                      return { ...service, steps: updatedSteps };
                                    });
                                    return { ...round, services: updatedServices };
                                  });
                                  setWorks(updatedWorks);
                                  setHasUnsavedChanges(true);
                                }}
                              />
                              <span className="text-[13px] text-gray-700 min-w-[64px]">
                                {st.unit || "-"}
                              </span>
                            </div>
                          </td>

                          <td className="border px-2 w-[16%] text-center">
                            {isTempEquipmentItem ? (
                              isRoundServiced(round) ? (
                                <span className={`text-[12px] font-medium ${st.isCollect ? "text-green-600" : "text-amber-700"}`}>
                                  {st.isCollect ? "เก็บคืน" : "วางไว้"}
                                </span>
                              ) : (
                              <div className="flex items-center justify-center whitespace-nowrap text-[12px] w-full">
                                <label className="flex items-center cursor-pointer select-none" style={{ gap: 2, paddingRight: 6 }}>
                                  <input
                                    type="radio"
                                    name={`collect-${st.id}`}
                                    checked={!st.isCollect}
                                    onChange={() => {
                                      const updatedWorks = works.map((round, roundIdx) => {
                                        if (roundIdx !== currentRoundIdx) return round;
                                        const updatedServices = round.services.map((service, serviceIdx) => {
                                          if (serviceIdx !== currentServiceIdx) return service;
                                          const updatedSteps = service.steps.map((step) => {
                                            if (step.id === st.id) {
                                              return { ...step, isCollect: false };
                                            }
                                            return step;
                                          });
                                          return { ...service, steps: updatedSteps };
                                        });
                                        return { ...round, services: updatedServices };
                                      });
                                      setWorks(updatedWorks);
                                      setHasUnsavedChanges(true);
                                    }}
                                    className="w-3.5 h-3.5 accent-[#E13F57]"
                                  />
                                  วางไว้
                                </label>
                                <label className="flex items-center cursor-pointer select-none" style={{ gap: 2 }}>
                                  <input
                                    type="radio"
                                    name={`collect-${st.id}`}
                                    checked={!!st.isCollect}
                                    onChange={() => {
                                      const updatedWorks = works.map((round, roundIdx) => {
                                        if (roundIdx !== currentRoundIdx) return round;
                                        const updatedServices = round.services.map((service, serviceIdx) => {
                                          if (serviceIdx !== currentServiceIdx) return service;
                                          const updatedSteps = service.steps.map((step) => {
                                            if (step.id === st.id) {
                                              return { ...step, isCollect: true };
                                            }
                                            return step;
                                          });
                                          return { ...service, steps: updatedSteps };
                                        });
                                        return { ...round, services: updatedServices };
                                      });
                                      setWorks(updatedWorks);
                                      setHasUnsavedChanges(true);
                                    }}
                                    className="w-3.5 h-3.5 accent-[#E13F57]"
                                  />
                                  เก็บคืน
                                </label>
                              </div>
                              )
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>

                          <td className="border px-2 w-[28%]">
                            <input 
                              className={inputClass} 
                              value={st.remark || ""} 
                              onChange={(e) => {
                                const updatedWorks = works.map((round, roundIdx) => {
                                  if (roundIdx !== currentRoundIdx) return round;
                                  const updatedServices = round.services.map((service, serviceIdx) => {
                                    if (serviceIdx !== currentServiceIdx) return service;
                                    const updatedSteps = service.steps.map((step) => {
                                      if (step.id === st.id) {
                                        return { ...step, remark: e.target.value };
                                      }
                                      return step;
                                    });
                                    return { ...service, steps: updatedSteps };
                                  });
                                  return { ...round, services: updatedServices };
                                });
                                setWorks(updatedWorks);
                                setHasUnsavedChanges(true);
                              }}
                            />
                          </td>

                          <td className="text-center border">
                            {!isRoundServiced(round) && (
                              <button
                                onClick={() => removeStep(rIdx, sIdx, st.id)}
                                className="text-red-500 hover:text-red-600"
                              >
                                <Icon icon="mdi:delete" className="w-5 h-5" />
                              </button>
                            )}
                          </td>

                        </tr>
                        );
                      })}
                    </tbody>

                  </table>
                </div>
              ))}


            </div>
          )}

          {/* ปุ่มบันทึก - แสดงที่มุมล่างขวาของ card ที่เป็น job ใหม่ (isDB: false) */}
          {round.isDB === false && (
            <div className="flex justify-end mt-4 pt-4 border-t px-4 pb-4">
              <button
                onClick={async () => {
                  if (!onSaveWorks) return;
                  try {
                    // capture ก่อน fetchContractData reset works
                    const assignInfo = round.assignInfo;
                    const roundNo = round.no;

                    if (round.status === "งานเสริม" && contractId) {
                      const key = `${round.no}-${round.date}`;
                      const stored = localStorage.getItem(`roundStatusMap_${contractId}`);
                      let statusMap = new Map();
                      if (stored) {
                        try { statusMap = new Map(Object.entries(JSON.parse(stored))); } catch {}
                      }
                      statusMap.set(key, "งานเสริม");
                      localStorage.setItem(`roundStatusMap_${contractId}`, JSON.stringify(Object.fromEntries(statusMap)));
                    }

                    await onSaveWorks(works);

                    // assign หลัง save สำเร็จ (works ถูก reload แล้ว ใช้ค่า capture ไว้แทน)
                    if (assignInfo?.date && assignInfo?.teamId) {
                      try {
                        const compId = localStorage.getItem("company");

                        // ตรวจ conflict
                        const conflictRes = await api.get("/get-assigned-jobs", {
                          params: { comp_id: compId, team_id: assignInfo.teamId, start_date: assignInfo.date, end_date: assignInfo.date },
                        });
                        if (conflictRes.data?.status === "OK" && Array.isArray(conflictRes.data.result)) {
                          const toMin = (t) => { const [h, m] = String(t).split(":").map(Number); return h * 60 + m; };
                          const newS = toMin(assignInfo.timeStart);
                          const newE = toMin(assignInfo.timeEnd);
                          const hasConflict = conflictRes.data.result.some((job) => {
                            if (!job.Time_Start || !job.Time_End) return false;
                            return newS < toMin(job.Time_End) && newE > toMin(job.Time_Start);
                          });
                          if (hasConflict) {
                            Swal.fire({
                              icon: "warning",
                              title: "เวลาซ้ำในปฏิทิน",
                              text: "ทีมนี้มีงานในช่วงเวลาที่เลือกแล้ว กรุณาแก้ไขเวลาในปฏิทิน",
                              confirmButtonText: "ตกลง",
                            });
                            return;
                          }
                        }

                        await api.post("/assign-job-calendar", {
                          Contract_Id: contractId,
                          Team_Id: Number(assignInfo.teamId),
                          Job_Date: assignInfo.date,
                          Time_Start: assignInfo.timeStart,
                          Time_End: assignInfo.timeEnd,
                          Comp_Id: compId ? parseInt(compId) : null,
                          Job_Status: "ลงงาน",
                          Round_No: roundNo,
                        });
                        onAssigned?.();
                      } catch (assignErr) {
                        Swal.fire({
                          icon: "warning",
                          title: "บันทึกสำเร็จ แต่ assign ไม่ได้",
                          text: assignErr.response?.data?.message || "กรุณา assign ในปฏิทินด้วยตนเอง",
                          confirmButtonText: "ตกลง",
                        });
                      }
                    }
                  } catch (error) {
                    Swal.fire({
                      icon: "error",
                      title: "เกิดข้อผิดพลาด",
                      text: error.message || "ไม่สามารถบันทึกข้อมูลได้",
                      confirmButtonText: "ตกลง",
                    });
                  }
                }}
                className="bg-[#E13F57] hover:bg-[#c7354a] text-white px-6 py-2 rounded-lg flex items-center gap-2 shadow-md font-medium"
              >
                <Icon icon="mdi:content-save" className="w-5 h-5" />
                บันทึก
              </button>
            </div>
          )}

        </div>
      ))}

      {/* ปุ่มบันทึกการแก้ไข - แสดงที่มุมขวาล่างของ card เมื่อมีการเปลี่ยนแปลง */}
      {(hasUnsavedChanges || hasUnsavedSteps) && (
        <div className="flex justify-end mt-6 pt-4 border-t">
          <button
            onClick={async () => {
              if (onSaveWorks) {
                try {
                  await onSaveWorks(works);
                  setHasUnsavedSteps(false);
                  setHasUnsavedChanges(false);
                  Swal.fire({
                    icon: "success",
                    title: "บันทึกสำเร็จ",
                    text: "บันทึกข้อมูลเรียบร้อยแล้ว",
                    timer: 2000,
                    showConfirmButton: false,
                  });
                } catch (error) {
                  console.error("Error saving works:", error);
                  Swal.fire({
                    icon: "error",
                    title: "เกิดข้อผิดพลาด",
                    text: error.message || "ไม่สามารถบันทึกข้อมูลได้",
                    confirmButtonText: "ตกลง",
                  });
                }
              }
            }}
            className="bg-[#E13F57] hover:bg-[#c7354a] text-white px-6 py-2 rounded-lg flex items-center gap-2 shadow-md font-medium"
          >
            <Icon icon="mdi:content-save" className="w-5 h-5" />
            บันทึกการแก้ไข
          </button>
        </div>
      )}

      <SelectStepModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        serviceData={modalServiceData}
        onSave={addStepsFromModal}
      />

      {/* Edit-assign modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between bg-[#E13F57] text-white px-5 py-3 rounded-t-xl">
              <h3 className="font-bold text-lg">
                กำหนดวันที่เข้าบริการ — ครั้งที่ {editRoundIdx != null ? works[editRoundIdx]?.no : ""}
              </h3>
              <button onClick={() => setShowEditModal(false)} className="text-2xl px-1 leading-none">×</button>
            </div>

            <div className="p-5 space-y-4">
              {/* radio mode */}
              <div className="flex gap-6">
                {[{ value: "month", label: "กำหนดเดือน" }, { value: "today", label: "กำหนดวันที่" }].map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 whitespace-nowrap">
                    <input
                      type="radio"
                      name="dateMode"
                      value={opt.value}
                      checked={editForm.dateMode === opt.value}
                      onChange={() => setEditForm((f) => ({
                        ...f,
                        dateMode: opt.value,
                        date: opt.value === "today" ? new Date().toISOString().split("T")[0] : f.date,
                      }))}
                      className="w-4 h-4 accent-[#E13F57] flex-shrink-0"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>

              {editForm.dateMode === "month" ? (
                /* กำหนดเดือน: month + year selects */
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">เดือน</label>
                    <select
                      value={editForm.month}
                      onChange={(e) => setEditForm((f) => ({ ...f, month: e.target.value }))}
                      className="w-full h-10 border border-gray-300 rounded-md px-3 text-sm focus:ring-1 focus:ring-[#E13F57] focus:outline-none"
                    >
                      <option value="">-- เดือน --</option>
                      {[["01","มกราคม"],["02","กุมภาพันธ์"],["03","มีนาคม"],["04","เมษายน"],
                        ["05","พฤษภาคม"],["06","มิถุนายน"],["07","กรกฎาคม"],["08","สิงหาคม"],
                        ["09","กันยายน"],["10","ตุลาคม"],["11","พฤศจิกายน"],["12","ธันวาคม"]
                      ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">ปี (พ.ศ.)</label>
                    <select
                      value={editForm.year}
                      onChange={(e) => setEditForm((f) => ({ ...f, year: e.target.value }))}
                      className="w-full h-10 border border-gray-300 rounded-md px-3 text-sm focus:ring-1 focus:ring-[#E13F57] focus:outline-none"
                    >
                      <option value="">-- ปี --</option>
                      {Array.from({ length: 5 }, (_, i) => {
                        const be = new Date().getFullYear() + 543 - 1 + i;
                        return <option key={be} value={String(be)}>{be}</option>;
                      })}
                    </select>
                  </div>
                </div>
              ) : (
                /* กำหนดวันนี้: date + team + time */
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">วันที่บริการ</label>
                    <ConfigProvider locale={thTH}>
                      <DatePicker
                        className="w-full h-10 text-sm"
                        value={editForm.date ? dayjs(editForm.date, "YYYY-MM-DD") : null}
                        onChange={(d) => setEditForm((f) => ({ ...f, date: d ? d.format("YYYY-MM-DD") : "" }))}
                        disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
                        format={(d) => d.format("DD/MM/") + (d.year() + 543)}
                        placeholder="วว/ดด/ปปปป"
                        allowClear={false}
                      />
                    </ConfigProvider>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ทีม <span className="text-red-500">*</span></label>
                    <select
                      value={editForm.teamId}
                      onChange={(e) => setEditForm((f) => ({ ...f, teamId: e.target.value }))}
                      className={`w-full h-10 border rounded-md px-3 text-sm focus:ring-1 focus:ring-[#E13F57] focus:outline-none ${!editForm.teamId ? "border-red-300" : "border-gray-300"}`}
                    >
                      <option value="">-- เลือกทีม --</option>
                      {teamList.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">เวลา</label>
                      <TimePicker
                        className="w-full h-10 text-sm"
                        format="HH:mm"
                        value={editForm.timeStart ? dayjs(editForm.timeStart, "HH:mm") : null}
                        onChange={(t) => setEditForm((f) => ({ ...f, timeStart: t ? t.format("HH:mm") : "" }))}
                        allowClear={false}
                        placeholder="ชั่วโมง:นาที"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">ถึงเวลา</label>
                      <TimePicker
                        className="w-full h-10 text-sm"
                        format="HH:mm"
                        value={editForm.timeEnd ? dayjs(editForm.timeEnd, "HH:mm") : null}
                        onChange={(t) => setEditForm((f) => ({ ...f, timeEnd: t ? t.format("HH:mm") : "" }))}
                        allowClear={false}
                        placeholder="ชั่วโมง:นาที"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 px-5 pb-5">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm"
              >
                ยกเลิก
              </button>
              <button
                onClick={async () => {
                  if (editForm.dateMode === "month") {
                    if (!editForm.month || !editForm.year) {
                      Swal.fire({ icon: "warning", title: "กรุณาเลือกเดือนและปี", confirmButtonText: "ตกลง" });
                      return;
                    }
                    const ceYear = parseInt(editForm.year) - 543;
                    const newDate = `${ceYear}-${editForm.month}-01`;
                    const updatedWorks = works.map((w, i) =>
                      i === editRoundIdx ? { ...w, date: newDate } : w
                    );
                    const normalizedWorks = normalizeRoundOrder(updatedWorks);
                    setWorks(normalizedWorks);
                    try {
                      await onSaveWorks(normalizedWorks);
                    } catch {}
                    setShowEditModal(false);
                  } else {
                    handleAssign();
                  }
                }}
                disabled={editLoading}
                className="px-5 py-2 rounded-lg bg-[#E13F57] hover:bg-[#c7354a] text-white text-sm font-medium disabled:opacity-60"
              >
                {editLoading ? "กำลังบันทึก..." : editForm.dateMode === "month" ? "บันทึก" : "Assign ทีม"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
